import { motion } from "framer-motion";
import { Gamepad2, Orbit, BookOpen } from "lucide-react";
import { useTranslation, Trans } from "react-i18next";
import { IMAGES } from "@/lib/constants";
import { FEATURES } from "@/lib/features";

const ECOSYSTEM = [
  { icon: Gamepad2, key: "games",    href: "/pilot" },
  ...(FEATURES.explorer ? [{ icon: Orbit, key: "explorer", href: "/explorer" }] : []),
  { icon: BookOpen, key: "lore",     href: null },
];

export default function About() {
  const { t } = useTranslation();
  return (
    <section id="about" data-testid="about-section" className="relative py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-start">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5 lg:sticky lg:top-28"
        >
          <p className="font-mono-alt text-[10px] tracking-[0.3em] text-secondary">{t("about.section_label")}</p>
          <h2 className="mt-4 font-display font-black tracking-tighter text-5xl md:text-6xl leading-[0.95]">
            {t("about.title_l1")}
            <br />
            {t("about.title_l2")}
          </h2>
          <div className="mt-6 h-1 w-24 bg-primary shadow-[0_0_20px_hsl(var(--primary))]" />
        </motion.div>

        <div className="lg:col-span-7 space-y-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-lg md:text-xl text-muted-foreground leading-relaxed"
          >
            <Trans i18nKey="about.paragraph_html" components={{ b: <span className="text-primary" /> }} />
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="grid sm:grid-cols-3 gap-4"
          >
            {ECOSYSTEM.map((it) => {
              const Icon = it.icon;
              const Tag = it.href ? "a" : "div";
              const extraProps = it.href ? { href: it.href, "data-testid": `ecosystem-link-${it.key}` } : {};
              return (
                <Tag
                  key={it.key}
                  {...extraProps}
                  className={`rounded-sm border border-primary/20 bg-card p-5 transition-colors group ${it.href ? "hover:border-primary/70 cursor-pointer" : "hover:border-primary/60"}`}
                >
                  <Icon className="h-6 w-6 text-primary group-hover:text-secondary transition-colors" />
                  <p className="mt-4 font-display font-bold text-lg flex items-center gap-2">
                    {t(`about.ecosystem.${it.key}.title`)}
                    {it.href && <span className="font-mono-alt text-[10px] tracking-widest text-primary">{t("about.open_link")}</span>}
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">{t(`about.ecosystem.${it.key}.desc`)}</p>
                </Tag>
              );
            })}
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative rounded-sm border border-primary/25 overflow-hidden group"
          >
            <img src={IMAGES.sectionBg} alt="cosmos" className="h-64 md:h-80 w-full object-cover opacity-70 group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <p className="font-mono-alt text-[10px] tracking-[0.3em] text-primary">{t("about.quote_label")}</p>
              <p className="mt-2 font-display font-black text-2xl md:text-3xl">
                <Trans i18nKey="about.quote_html" components={{ b: <span className="text-primary neon-glow-primary" /> }} />
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
